import React, { useState } from 'lib/react';
import {
  EVAlert, EVTypography, makeStyles, EVCheckCircleOutlineIcon, EVButton,
} from 'lib/components';
import { useTranslation } from 'lib/translation';
import clsx from 'lib/clsx';
import {
  SUCCESS, ERROR,
} from 'constants/AppConstants';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'lib/redux';
import downloadReportFilesService from 'services/downloadReportFiles.service';
import { updatePitchStatus } from '../PitchEditor.actions';

const useStyles = makeStyles((theme) => ({
  successAlert: {
    backgroundColor: theme.evPalette.lime[100],
    borderBottom: `1px solid ${theme.evPalette.lime[700]}`,
  },
  errorAlert: {
    backgroundColor: theme.evPalette.rose[100],
    borderBottom: `1px solid ${theme.evPalette.rose[900]}`,
  },
  alert: {
    padding: '8px 16px 8px 16px',
    borderRadius: '1px',
    '@media (min-width: 640px)': {
      padding: '16px',
      border: 'none',
      borderRadius: '4px',
    },
  },
  alertMsg: {
    display: 'flex',
    flexDirection: 'column',
    color: theme.evPalette.charcoal[900],
    gap: '8px',
    fontSize: '14px',
    '&>.message': {
      fontWeight: 400,
      lineHeight: '18px',
      fontSize: '14px',
      '&>a': {
        color: theme.evPalette.ocean[900],
      },
    },
    '@media (min-width: 640px)': {
      gap: '16px',
      '&>.message': {
        fontWeight: 400,
        fontSize: '14px',
        lineHeight: '24px',
      },
    },
  },
  successAlertIcon: { '& svg': { color: theme.evPalette.lime[700] }, alignItems: 'center' },
  errorAlertIcon: { '& svg': { color: theme.evPalette.rose[900] }, alignItems: 'center' },
  closeBtn: { alignItems: 'flex-start' },
  alertMsgText: {
    gap: '4px',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    '@media (max-width: 1024px)': {
      flexWrap: 'wrap',
    },
  },
  hereBtn: {
    background: 'none',
    textDecoration: 'underline',
    fontSize: '14px',
    fontWeight: '600',
    minWidth: 'fit-content',
    minHeight: 'fit-content',
    padding: '0px',
    margin: '0px',
    alignItems: 'center',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
}));

function PitchEditorAlert(props) {
  const { t } = useTranslation();
  const classes = useStyles();
  const { handleSaveReport } = props;
  const dispatch = useDispatch();
  const [downloadReportStatus, setdownloadReportStatus] = useState(null);
  const {
    reportId, editPitchStatus, updatedReportFileId,
  } = useSelector((state) => state.pitchEditor);

  const handleDownloadClick = async () => {
    const requestBody = {
      files: [{
        fileID: updatedReportFileId,
        fileText: `${reportId}.pdf`,
        reportID: Number(reportId),
      }],
    };
    downloadReportFilesService(requestBody)
      .then(({ ok }) => {
        if (ok) {
          dispatch(updatePitchStatus('', updatedReportFileId));
          setdownloadReportStatus(null);
        } else {
          dispatch(updatePitchStatus('', updatedReportFileId));
          setdownloadReportStatus(ERROR);
        }
      });
  };

  const handleRetryClick = () => {
    if (editPitchStatus === ERROR) {
      handleSaveReport();
    } else if (downloadReportStatus === ERROR) {
      handleDownloadClick();
    }
  };

  if (editPitchStatus === SUCCESS) {
    return (
      <EVAlert
        severity={SUCCESS}
        className={classes.alert}
        classes={{
          icon: classes.successAlertIcon, message: classes.alertMsg, root: classes.successAlert, action: clsx(classes.closeBtn, classes.successAlertIcon),
        }}
        iconMapping={{ success: <EVCheckCircleOutlineIcon /> }}
      >
        <EVTypography className={classes.alertMsgText}>
          {t('pitch_editor__alert_success_message_1')}
          <EVButton
            data-testid="here-btn-success"
            variant="text"
            className={classes.hereBtn}
            onClick={() => handleDownloadClick()}
          >
            {t('coming_soon__here')}
          </EVButton>
          {t('pitch_editor__alert_success_message_2')}
        </EVTypography>
      </EVAlert>
    );
  }
  if (editPitchStatus === ERROR || downloadReportStatus === ERROR) {
    return (
      <EVAlert
        severity={ERROR}
        className={classes.alert}
        classes={{
          icon: classes.errorAlertIcon, message: classes.alertMsg, root: classes.errorAlert, action: clsx(classes.closeBtn, classes.errorAlertIcon),
        }}
      >
        <EVTypography className={classes.alertMsgText}>
          {editPitchStatus === ERROR ? t('pitch_editor__alert_error_message_1') : t('pitch_editor_download_report__alert_error_message_1')}
          <EVButton
            data-testid="here-btn-error"
            variant="text"
            className={classes.hereBtn}
            onClick={() => handleRetryClick()}
          >
            {t('coming_soon__here')}
          </EVButton>
          {t('pitch_editor__alert_error_message_2')}
        </EVTypography>
      </EVAlert>
    );
  }

  return null;
}

PitchEditorAlert.propTypes = {
  handleSaveReport: PropTypes.func.isRequired,
};

export default PitchEditorAlert;
